import { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { RequestStatus, useFetch, useRequest } from '../../../hooks/fetch-request.hook';
import { useTexts } from '../../../hooks/texts.hook';
import ValidationSummary from '../../Shared/ValidationSummary';
import ValidationInfo from '../../../validation/validation-info';
import ApiClientService from '../../../services/api-client.service';
import Alert from '../../Shared/Alert';
import { AlertType } from '../../Shared/Alert/models/alert.model';
import { SanitizerService } from '../../../services/sanitizer.service';
import IContactInformation, { emptyContactInformation } from '../../../models/contact-information.model';
import { LanguageService } from '../../../services/language.service';
import PersonalIdentityNumberLookup from '../../Shared/PersonalIdentityNumberLookup';
import { PersonalIdentityNumberFeature } from '../../Shared/PersonalIdentityNumberLookup/personal-identity-lookup-feature.enum';
import TextFieldFormGroup from '../../Shared/TextFieldFormGroup';
import { IConsentTemplateTranslation } from '../models/consent-template.model';

import Spinner from '../../Shared/Spinner';
import ConsentTemplateApiModel from '../models/consent-template.api-model';
import { isOlderThan13 } from './create-account.helper';

export interface ICreateAccountTexts {
  consentHtml: string;
}

export type ICreateAccountProps = { texts: ICreateAccountTexts };

interface ILoginStatusResponse {
  isLoggedIn: boolean;
}

interface ICreateAccountRequest {
  personalIdentityNumber: string;
  email: string;
}

interface ICreateAccountResponse {
  passwordResetUrl: string;
}

function CreateAccount({ texts }: ICreateAccountProps) {
  const { t } = useTexts();

  // === REFS ===
  const createAccountErrorRef = useRef<HTMLDivElement>(null);

  // === STATE ===
  const loginStatusRequest = useFetch<ILoginStatusResponse>({
    url: '/api/mittkonto/privatecustomers/current/isauthenticated',
  });
  const isLoggedOut = loginStatusRequest.data?.isLoggedIn === false;
  const integrityPolicyRequest = useFetch<ConsentTemplateApiModel>({
    url: '/api/mittkonto/consenttemplates/integritypolicy',
    enabled: isLoggedOut,
  });
  const createAccountRequest = useRequest({
    request: (request: ICreateAccountRequest, signal) => ApiClientService.post<ICreateAccountResponse>('/api/mittkonto/privatecustomers', request, signal),
  });

  const [emailState, setEmailState] = useState({ value: '', validationInfo: new ValidationInfo() });
  const [personalIdentityNumberLookupState, setPersonalIdentityNumberLookupState] = useState<{
    value: IContactInformation;
    validationInfo: ValidationInfo;
  }>({ value: emptyContactInformation, validationInfo: new ValidationInfo() });
  const [personalIdValidationInfo, setPersonalIdValidationInfo] = useState(new ValidationInfo());

  // === VARIABLES ===
  const offerType = new URLSearchParams(window.location.search).get('erbjudande');
  const validationSummaryErrors = [...emailState.validationInfo.getErrorsWithPrefix(), ...personalIdValidationInfo.getErrorsWithPrefix()];
  const loadingDataInProgress = loginStatusRequest.status === RequestStatus.loading || integrityPolicyRequest.status === RequestStatus.loading;
  const integrityPolicyConsent = integrityPolicyRequest.data ? ConsentTemplateApiModel.mapToBusinessModel(integrityPolicyRequest.data) : undefined;
  const initialDataFailed = loginStatusRequest.status === RequestStatus.failed || integrityPolicyRequest.status === RequestStatus.failed;
  const canCreateAccount = isLoggedOut && !!integrityPolicyConsent;
  // Stays disabled after success while the browser navigates to the password page
  const createAccountInProgress = createAccountRequest.status === RequestStatus.loading || createAccountRequest.status === RequestStatus.success;

  // === HANDLERS ===
  function validateForm(): boolean {
    const emailErrors: string[] = [];
    if (!emailState.value) emailErrors.push(t('E-postadress är obligatoriskt.', 'Email address is required.'));
    else if (emailState.value.length > 254) emailErrors.push(t('E-postadressen får inte vara längre än 254 tecken.', 'The email address cannot be longer than 254 characters.'));
    else if (!isValidEmail(emailState.value))
      emailErrors.push(t('Kontrollera att e-postadressen är i formatet: namn@exempel.com.', 'Please check that the email address is in the format: name@example.com.'));

    const personalIdErrors: string[] = [];
    if (personalIdentityNumberLookupState.value.valid && !isOlderThan13(personalIdentityNumberLookupState.value.personalIdentityNumber))
      personalIdErrors.push(t('Du måste vara över 13 år för att kunna skapa ett konto.', 'You must be over 13 years old to create an account.'));

    flushSync(() => {
      setEmailState((previousState) => ({ ...previousState, validationInfo: new ValidationInfo(t('E-post', 'Email') + ': ', emailErrors) }));
      setPersonalIdValidationInfo(new ValidationInfo(t('Personnummer', 'Personal identity number') + ': ', personalIdErrors));
    });

    if (emailErrors.length > 0 || personalIdErrors.length > 0) {
      document.getElementById('create-account-validation-summary')?.focus();
      return false;
    }
    return true;
  }

  async function handleCreateAccountClick() {
    if (createAccountInProgress || !canCreateAccount || !validateForm()) return;
    const result = await createAccountRequest.execute({ personalIdentityNumber: personalIdentityNumberLookupState.value.personalIdentityNumber, email: emailState.value });
    if (result.status === RequestStatus.success) window.location.href = result.data.passwordResetUrl;
    else if (result.status === RequestStatus.failed) createAccountErrorRef.current?.focus();
  }

  function t1(func: (translation: IConsentTemplateTranslation) => string): string {
    if (!integrityPolicyConsent) return '';
    if (LanguageService.getCurrentLanguage() === 'en') return func(integrityPolicyConsent.englishTranslations);
    return func(integrityPolicyConsent.swedishTranslations);
  }

  return (
    <div className="page-create-account">
      {loadingDataInProgress && <Spinner className="my-4 my-md-5" delayInMs={0} />}
      {!loadingDataInProgress && initialDataFailed && (
        <Alert
          show={true}
          heading={t('Det gick inte att hämta uppgifterna som krävs för att skapa konto.', 'Failed to retrieve the information required to create an account.')}
          headingTag="div"
          type={AlertType.Error}
          closable={false}
          className="mb-3"
        />
      )}
      <Alert
        show={!loadingDataInProgress && loginStatusRequest.data?.isLoggedIn === true}
        heading={t('Du är redan inloggad.', 'You are already logged in.')}
        headingTag="div"
        type={AlertType.Default}
        closable={false}
      />
      {!loadingDataInProgress && canCreateAccount && (
        <div>
          {offerType === 'senior' && (
            <div className="border border-vtd-gray-700 rounded p-3 mb-4">
              <h2>{t('Beställ Västtrafiks seniorbiljett', 'Order Västtrafik’s senior ticket')}</h2>
              <p>{t('Här beställer du din seniorbiljett. Skapa ett konto för att komma till erbjudandet.', 'Here you can order your senior ticket. Create an account to access the offer.')}</p>
              <a href="https://www.vasttrafik.se/biljetter/andra-biljetter/seniorkort/">{t('Mer information om seniorbiljetten', 'More information about the senior ticket')}</a>
            </div>
          )}

          {validationSummaryErrors.length > 0 && <h2 className="visually-hidden">{t('Felmeddelanden', 'Error messages')}</h2>}
          <ValidationSummary id="create-account-validation-summary" validationSummaryErrors={validationSummaryErrors} className="mb-4" />
          <div tabIndex={-1} ref={createAccountErrorRef}>
            <Alert
              show={createAccountRequest.status === RequestStatus.failed}
              heading={t('Det gick inte att skapa ett konto', 'Failed to create an account')}
              content={`<p>${t('Det kan bero på att det redan finns ett konto med den e-postadress eller det personnummer som du angav.', 'This may be because an account already exists with the email address or personal identity number you entered.')}</p><p>${t('Om du redan har ett konto men inte lyckats logga in kan du få hjälp av kundservice.', 'If you already have an account but have not been able to log in, you can get help from customer service.')}</p>`}
              headingTag="div"
              type={AlertType.Error}
              closable={false}
              className="mb-3"
            />
          </div>
          <TextFieldFormGroup
            inputId="email"
            labelText={t('E-post', 'Email')}
            required={true}
            type="email"
            autocomplete="email"
            className="mb-3"
            state={emailState}
            onChange={(value) => setEmailState((previousState) => ({ ...previousState, value, validationInfo: new ValidationInfo() }))}
          />
          <PersonalIdentityNumberLookup
            state={personalIdentityNumberLookupState}
            feature={PersonalIdentityNumberFeature.CreatePrivateCustomerAccount}
            noPersonalIdentityNumberText=""
            lookupButtonText={t('Hämta personuppgifter', 'Get personal data')}
            onChange={(value) => {
              setPersonalIdentityNumberLookupState((previousState) => ({ ...previousState, value }));
              setPersonalIdValidationInfo(new ValidationInfo());
            }}
          />
          {personalIdValidationInfo.isInvalid && <div className="invalid-feedback d-block mb-3">{personalIdValidationInfo.errors[0]}</div>}
          <div className="mb-3" dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(texts.consentHtml) }} />
          <div className="mb-3">
            {t1((x) => x.text)} <a href={t1((x) => x.url)}>{t('Västtrafiks integritetspolicy', 'Västtrafiks privacy policy')}</a>.
          </div>
          {personalIdentityNumberLookupState.value.valid && (
            <>
              <span className="visually-hidden" role="status" aria-live="polite" aria-atomic="true">
                {createAccountInProgress ? t('Skapar ditt konto...', 'Creating your account...') : ''}
              </span>
              <button type="button" className="btn btn-primary w-100" disabled={createAccountInProgress} onClick={handleCreateAccountClick}>
                {createAccountInProgress ? t('Skapar ditt konto...', 'Creating your account...') : t('Fortsätt och skapa lösenord', 'Continue and create password')}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function isValidEmail(email: string): boolean {
  const an = 'a-zA-Z0-9';
  return new RegExp(`^[${an}][${an}_.+-]*[${an}_+-]@([${an}-]+\\.)*[${an}-]+(\\.[${an}-]+)+$|^[${an}]@([${an}-]+\\.)*[${an}-]+(\\.[${an}-]+)+$`).test(email);
}

export default CreateAccount;
